import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

const RESIZE_DEBOUNCE_MS = 50;

/**
 * A custom element that manages a dialog.
 */
export class DialogComponent extends HTMLElement {
  /** @type {Record<string, string>} CSS selectors keyed by logical name. */
  selectors = {
    dialog: 'dialog',
    closeButton: '[dialog-close]',
  };
  /** @type {Record<string, Element | null>} Cached DOM elements resolved from selectors. */
  elements = {};

  connectedCallback() {
    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    // Loop selectors and create matching elements
    Object.keys(this.selectors).forEach((key) => {
      this.elements[key] = this.querySelector(this.selectors[key]);
    });

    // Attach event listener to close button
    const { closeButton } = this.elements;
    if (closeButton) {
      closeButton.addEventListener('click', this.close);
    }

    if (!this.elements.dialog) {
      console.warn('DialogComponent: no <dialog> element found', this);
      return;
    }
  }

  disconnectedCallback() {
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.close();
    }
  }, RESIZE_DEBOUNCE_MS);

  /**
   * Shows the dialog.
   */
  open() {
    const { dialog } = this.elements;

    if (dialog.open) return;

    dialog.showModal();

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    // Defer listener attachment to the next microtask so the current
    // click event that triggered `open()` doesn't immediately close the dialog.
    setTimeout(() => {
      dialog.classList.add('dialog-open');
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  close = async () => {
    const { dialog } = this.elements;

    if (!dialog.open) return;

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    dialog.classList.remove('dialog-open');
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.elements.dialog.open) {
      this.close();
    } else {
      this.open();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick = (event) => {
    const { dialog } = this.elements;

    if (isClickedOutside(event, dialog)) {
      this.close();
    }
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component'))
  customElements.define('dialog-component', DialogComponent);

function handleScrollLockToggle(event) {
  const { target } = event;
  if (
    (target instanceof HTMLDialogElement || target instanceof HTMLDetailsElement) &&
    target.hasAttribute('scroll-lock')
  ) {
    document.documentElement.toggleAttribute('scroll-lock', target.open);
  }
}

document.addEventListener('toggle', handleScrollLockToggle, { capture: true });
